import { useQueries, useQuery } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { getCompanyDateRange, getCompanyReviewCounts, getSourceList } from 'apis/competitorPulseApis';
import { ORG_TYPES } from "components/Common/Constants";
import { useRequiredAccess } from 'hooks/useRequiredAccess';
import { useEffect, useState } from 'react';
import CompanyCompareFilter from './CompanyCompareFilter';
import CompareReviewAnalysis from './ReviewAnalysis';
import { defaultColor } from '../constants';

const MarketPulseComparison = ({ onChangeTab, companiesList }) => {
  const checkAccess = useRequiredAccess([], [ORG_TYPES.ADMINISTRATOR]);

  useEffect(() => {
    checkAccess();
  }, []);
  
  const [isCompareClicked, setIsCompareClicked] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);

  const [filterValue, setFilterValue] = useState({
    companies: [
      {
        company: null,
        minDate: null,
        maxDate: null,
        startDate: null,
        endDate: null,
        bikeModel: null,
      },
      {
        company: null,
        minDate: null,
        maxDate: null,
        startDate: null,
        endDate: null,
        bikeModel: null,
      },
    ],
    sourceList: null,
    sentiment: null,
  });

  const [barChartFilters, setBarChartFilters] = useState({
    companies: [
      {
        company: null,
        startDate: null,
        endDate: null,
        bikeModel: null,
      },
      {
        company: null,
        startDate: null,
        endDate: null,
        bikeModel: null,
      },
    ],
  });
  // Set initial load flag when companies are available
  useEffect(() => {
    if (
      filterValue.companies[0]?.company &&
      filterValue.companies[1]?.company &&
      !initialLoad
    ) {
      setInitialLoad(true);
      setBarChartFilters({
        companies: filterValue.companies.map((company) => ({
          company: company.company,
          startDate: company.startDate,
          endDate: company.endDate,
          bikeModel: company.bikeModel,
        })),
      });
    }
  }, [filterValue.companies, initialLoad]);

  const companyQueries = useQueries({
    queries: filterValue.companies.map((company) => ({
      queryKey: [
        "companyData",
        company?.company,
        company?.startDate,
        company?.endDate,
      ],
      queryFn: () =>
        getCompanyDateRange(
          company.company,
          Math.floor(company.startDate / 1000),
          Math.floor(company.endDate / 1000)
        ),
      keepPreviousData: true,
      enabled: !!(company?.company && company?.startDate && company?.endDate),
      refetchOnWindowFocus: false,
    })),
  });

const [company1, company2] = companyQueries;
const company1Data = company1.data;
const company2Data = company2.data;
const company1DataLoading = company1.isLoading;
const company2DataLoading = company2.isLoading;
  

  const reviewCountQueries = useQueries({
    queries: filterValue.companies.map((company, index) => ({
      queryKey: ["ReviewCounts", company?.company],
      queryFn: () => getCompanyReviewCounts(company.company),
      keepPreviousData: true,
      enabled: !!company?.company,
      refetchOnWindowFocus: false,
    })),
  });
  const [reviewCountCompany1, reviewCountCompany2] = reviewCountQueries;
  const reviewcountcompany1 = reviewCountCompany1.data;
  const reviewcountcompany2 = reviewCountCompany2.data;

  const {
    data: source,
    isLoading: sourceLoading,
  } = useQuery({
    queryKey: ["source"],
    queryFn: () => getSourceList(),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const onFilterChange = (name, index, value, total) => {
    if (name === "dates") {
      setFilterValue((prev) => {
        const newCompanies = [...prev.companies];
        newCompanies[index] = {
          ...newCompanies[index],
          startDate: value[0],
          endDate: value[1],
        };
        return {
          ...prev,
          companies: newCompanies,
          sourceList: null,
          sentiment: null,
        };
      });
    } else if (name === "bikeModel") {
      const [firstColumn, ...otherColumns] = value;
      const selectedColumn =
        !value?.length ||
        otherColumns.at(-1) === null ||
        value.filter((item) => item !== null).length === total
          ? null
          : firstColumn === null
          ? otherColumns
          : value;

      setFilterValue((prev) => {
        const newCompanies = [...prev.companies];
        newCompanies[index] = {
          ...newCompanies[index],
          bikeModel: selectedColumn,
        };
        return {
          ...prev,
          companies: newCompanies,
        };
      });
    } else if (name === "sourceList") {
      const [firstColumn, ...otherColumns] = value;
      const selectedColumn =
        !value?.length ||
        otherColumns.at(-1) === null ||
        value.filter((item) => item !== null).length === total
          ? null
          : firstColumn === null
          ? otherColumns
          : value;

      setFilterValue((prev) => ({
        ...prev,
        sourceList: selectedColumn,
      }));

      setBarChartFilters((prev) => ({
        ...prev,
        sourceList: selectedColumn,
      }));
    } else if (name === "sentiment") {
      setFilterValue((prev) => ({
        ...prev,
        sentiment: value,
      }));

      setBarChartFilters((prev) => ({
        ...prev,
        sentiment: value,
      }));
    }
  };
  
  const handleCompareClick = () => {
    setIsCompareClicked(true);

    setBarChartFilters({
      companies: filterValue.companies.map((company) => ({
        company: company.company,
        startDate: company.startDate,
        endDate: company.endDate,
        bikeModel: company.bikeModel,
      })),
    });
  };

  return (
    <Row justify="center" className="competitor-pulse-page h100vh">
      <Col span={24}>
        <CompanyCompareFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onFilterChange={onFilterChange}
          onCompareClick={handleCompareClick}
          companyReviewData={[company1Data, company2Data]}
          companiesList={companiesList}
          companiesLoading={companiesList?.isLoading || false}
          loadingStates={[company1DataLoading, company2DataLoading]}
          reviewCounts={[reviewcountcompany1, reviewcountcompany2]}
        />
      </Col>

      <Col span={24} className="mt1">
      <CompareReviewAnalysis
          source={source}
          loading={sourceLoading}
          onFilterChange={onFilterChange}
          filterValue={filterValue}
          barChartFilters={barChartFilters}
          selectedColor={defaultColor}
          isCompareClicked={isCompareClicked}
          initialLoad={initialLoad}
        />
      </Col>
    </Row>
  );
};

export default MarketPulseComparison;